import {
	Button,
	ChipConfirmModal,
	ChipModal,
	ChipModalBody,
	ChipModalFooter,
	ChipModalHeader,
	Input,
	Label,
	Modal,
	ModalContent,
	ModalTitle,
	PublishingTimeCalendar,
	useCalendarDragSource,
} from "@ondit/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { type ReactElement, useId, useState } from "react";
import { toast } from "sonner";
import { useAccess } from "~/core/access";
import { ApiError, api } from "~/core/api";
import type { DistributionPost, PublishingPlatform } from "~/core/api/distribution";
import { pick, useI18n, useT } from "~/core/i18n";
import { brandContracts } from "~/core/publishing";
import { keys } from "~/core/query";
import { SearchSelect } from "~/core/ui/search-select";
import { Async } from "~/core/ui/states";
import {
	type CalendarPost,
	calendarDays,
	calendarHour,
	calendarPosts,
	canMoveCalendarPost,
	dayKey,
	localDateTime,
	needsConfirming,
	publishableNow,
} from "./calendar-data";
import { PublishingContentEditor } from "./content-editor";
import { PlatformPreview } from "~/common/publishing/platform-preview";
import { PublishConfirm } from "./publish-confirm";
import { ResultLink } from "./result-link";
import "./publishing.css";

export function PublishingCalendar({
	entityId,
	posts,
	platforms,
	pages,
	onEdit,
}: {
	entityId: string;
	posts: DistributionPost[];
	platforms: PublishingPlatform[];
	pages: { id: string; title: string; status: string }[];
	onEdit: (page: string, platform: string, task?: string) => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const [anchor, setAnchor] = useState(() => new Date());
	const [view, setView] = useState<"day" | "week" | "month">("week");
	const [filter, setFilter] = useState("");
	const [detail, setDetail] = useState<CalendarPost | null>(null);
	const [linkEntry, setLinkEntry] = useState<CalendarPost | null>(null);
	const [plan, setPlan] = useState<{ day: string; post?: CalendarPost } | null>(null);
	const [panel, setPanel] = useState<"unscheduled" | "awaiting" | null>(null);
	const [confirmPublish, setConfirmPublish] = useState(false);
	const tasks = useQuery({
		queryKey: [...keys.entity(entityId), "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
		refetchInterval: 5000,
	});
	const queryClient = useQueryClient();
	const days = calendarDays(anchor, view);
	const names = new Map(platforms.map((p) => [p.id, p.name]));
	const rows = calendarPosts(posts, tasks.data?.tasks ?? []).filter((p) => !filter || p.platform === filter);
	const stateLabel = (row: CalendarPost) =>
		t(
			["planned", "draft", "recorded", "scheduled"].includes(row.state)
				? `publishCalendar.${row.state}`
				: `publishing.platform.state.${row.state}`,
		);
	const move = (n: number) =>
		setAnchor((a) =>
			view === "month"
				? new Date(a.getFullYear(), a.getMonth() + n, 1)
				: new Date(a.getFullYear(), a.getMonth(), a.getDate() + n * (view === "week" ? 7 : 1)),
		);
	const unscheduled = rows.filter((row) => !row.date);
	const awaiting = rows.filter(needsConfirming);
	const publishable =
		detail && publishableNow(detail, brandContracts(platforms).byId(detail.platform), tasks.data?.accounts ?? []);
	const schedule = async (id: string, date: Date) => {
		const row = rows.find((item) => item.id === id);
		if (!row || !canMoveCalendarPost(row)) throw Error("Locked publication");
		try {
			const moved = new Date(date);
			/* A month cell carries no hour: keep the one the post already had, and
			   start an unscheduled one at the 09:00 the plan dialog also defaults to. */
			if (view === "month") moved.setHours(row.date ? new Date(row.date).getHours() : 9, 0, 0, 0);
			const at = moved.toISOString();
			if (row.task?.schedule_state === "waiting")
				await api.movePlatformSchedule(entityId, row.task.id, at, row.task.scheduled_at!);
			else
				await api.planDistribution(entityId, {
					page_id: row.page,
					platform_id: row.platform,
					planned_at: at,
				});
			await queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
			throw error;
		}
	};
	const unplan = async (row: CalendarPost) => {
		try {
			await api.planDistribution(entityId, { page_id: row.page, platform_id: row.platform, planned_at: null });
			setDetail(null);
			await queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
		}
	};
	const renderPost = (row: CalendarPost) => (
		<button
			type="button"
			aria-label={`${names.get(row.platform) ?? row.platform} · ${row.title} · ${stateLabel(row)}`}
			className="publisher-calendar-post"
			data-calendar-drag={editor && canMoveCalendarPost(row) ? row.id : undefined}
			key={row.id}
			onClick={() => setDetail(row)}
		>
			<span className="publisher-calendar-meta">
				{row.date && (
					<time dateTime={row.date}>
						{new Date(row.date).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}
					</time>
				)}
				<span>{names.get(row.platform) ?? row.platform}</span>
			</span>
			<strong>{row.title}</strong>
			{row.body && <span className="publisher-calendar-excerpt">{row.body.slice(0, 180)}</span>}
			<span className="hint">{stateLabel(row)}</span>
		</button>
	);
	return (
		<div className="publisher-calendar stack">
			<div className="publisher-calendar-toolbar">
				<div className="platform-actions">
					<Button variant="ghost" aria-label={t("publishCalendar.previous")} onClick={() => move(-1)}>
						<ChevronLeft />
					</Button>
					<strong aria-live="polite">
						{view === "month"
							? anchor.toLocaleDateString(locale, { year: "numeric", month: "long" })
							: `${days[0].toLocaleDateString(locale, { month: "short", day: "numeric" })} — ${days.at(-1)!.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" })}`}
					</strong>
					<Button variant="ghost" aria-label={t("publishCalendar.next")} onClick={() => move(1)}>
						<ChevronRight />
					</Button>
					<Button variant="default" onClick={() => setAnchor(new Date())}>
						{t("publishCalendar.today")}
					</Button>
					<div className="publisher-calendar-filter">
						<SearchSelect
							label={t("distribution.platform")}
							value={filter}
							onChange={setFilter}
							placeholder={t("publishCalendar.allPlatforms")}
							searchLabel={t("distribution.search")}
							emptyLabel={t("table.noMatch")}
							options={[
								{ value: "", label: t("publishCalendar.allPlatforms") },
								...platforms.map((p) => ({ value: p.id, label: p.name })),
							]}
						/>
					</div>
				</div>
				<div className="platform-actions">
					<fieldset aria-label={t("publishCalendar.view")} className="bare-fieldset platform-actions">
						{(["day", "week", "month"] as const).map((v) => (
							<Button
								key={v}
								variant={view === v ? "default" : "ghost"}
								aria-pressed={view === v}
								onClick={() => setView(v)}
							>
								{t(`publishCalendar.${v}`)}
							</Button>
						))}
					</fieldset>
					{awaiting.length > 0 && (
						<Button
							variant="ghost"
							className="publisher-calendar-awaiting"
							aria-expanded={panel === "awaiting"}
							onClick={() => setPanel("awaiting")}
						>
							{t("publishCalendar.awaiting", { count: awaiting.length })}
						</Button>
					)}
					<Button variant="ghost" aria-expanded={panel === "unscheduled"} onClick={() => setPanel("unscheduled")}>
						{t("publishCalendar.unscheduled")} · {unscheduled.length}
					</Button>
					<Button variant="primary" disabled={!editor} onClick={() => setPlan({ day: dayKey(anchor) })}>
						{t("publishCalendar.new")}
					</Button>
				</div>
			</div>
			<Async state={tasks}>
				{() => (
					<>
						<PublishingTimeCalendar
							label={t("publishCalendar.title")}
							date={anchor}
							view={view}
							locale={locale}
							events={rows
								.filter((row) => row.date)
								.map((row) => ({
									id: row.id,
									title: row.title,
									start: row.date!,
									platform: names.get(row.platform) ?? row.platform,
									status: stateLabel(row),
									tone: needsConfirming(row) ? "attention" : undefined,
									editable: editor && canMoveCalendarPost(row),
								}))}
							onMove={editor ? schedule : undefined}
							onDrop={editor ? (id, date) => void schedule(id, date).catch(() => {}) : undefined}
							onOpen={(id) => setDetail(rows.find((row) => row.id === id) ?? null)}
							onSelect={
								editor
									? (date, allDay) =>
											setPlan({ day: allDay ? dayKey(date) : localDateTime(calendarHour(date).toISOString()) })
									: undefined
							}
						/>
						{!rows.some((row) => row.date && days.some((day) => dayKey(day) === dayKey(new Date(row.date!)))) && (
							<p className="hint">{t("publishCalendar.empty")}</p>
						)}
					</>
				)}
			</Async>
			{panel && (
				<PostsPanel
					title={
						panel === "awaiting"
							? t("publishCalendar.awaiting", { count: awaiting.length })
							: `${t("publishCalendar.unscheduled")} · ${unscheduled.length}`
					}
					hint={t(panel === "awaiting" ? "publishCalendar.willNotSend" : "publishCalendar.dragHelp")}
					rows={panel === "awaiting" ? awaiting : unscheduled}
					emptyLabel={t(panel === "awaiting" ? "publishCalendar.noAwaiting" : "publishCalendar.noDrafts")}
					renderPost={renderPost}
					onConfirm={
						panel === "awaiting" && editor
							? (row) => {
									setPanel(null);
									onEdit(row.page, row.platform, row.task?.id);
								}
							: undefined
					}
					onClose={() => setPanel(null)}
				/>
			)}
			{plan && (
				<PlanModal
					entityId={entityId}
					day={plan.day}
					post={plan.post}
					pages={pages}
					platforms={platforms}
					onClose={() => setPlan(null)}
				/>
			)}
			{detail && publishable && (
				<PublishConfirm
					open={confirmPublish}
					onOpenChange={setConfirmPublish}
					entityId={entityId}
					platform={{ name: names.get(detail.platform) ?? detail.platform }}
					credentialId={publishable.account.id}
					detail={<p>{publishable.account.name}</p>}
					resolveTask={async () => publishable.task}
					onPublished={(_, result) => {
						toast.success(t(`publishing.platform.state.${result.state}`));
						setDetail(null);
						void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
					}}
				/>
			)}
			{detail && (
				<ChipModal
					open
					onOpenChange={(open) => {
						if (!open) setDetail(null);
					}}
					size="full"
					className="publisher-calendar-dialog"
					srTitle={detail.title}
				>
					<ChipModalHeader onClose={() => setDetail(null)} closeAriaLabel={t("common.close")}>
						{t("publishCalendar.details")}
					</ChipModalHeader>
					<ChipModalBody className="px-(--s-8) publisher-detail-view">
						<div className="publisher-detail-summary">
							<h3>{detail.title}</h3>
							<p>
								{names.get(detail.platform)} · {stateLabel(detail)}
							</p>
							{detail.plannedAt && (
								<p className="hint">
									{t("publishCalendar.plannedTime")} ·{" "}
									{new Date(detail.plannedAt).toLocaleString(locale, { timeZoneName: "short" })}
								</p>
							)}
							{(!canMoveCalendarPost(detail) || detail.task?.schedule_state === "waiting") && (
								<p role="note">{t("publishCalendar.lockedContent")}</p>
							)}
							{needsConfirming(detail) && <p role="note">{t("publishCalendar.willNotSend")}</p>}
						</div>
						<div className="publisher-detail-content">
							<CalendarContentEditor
								viewOnly
								entityId={entityId}
								row={detail}
								platform={platforms.find((p) => p.id === detail.platform)}
							/>
						</div>
						{detail.url && (
							<a href={detail.url} target="_blank" rel="noreferrer">
								{t("publishFlow.openResult")}
							</a>
						)}
						<div className="platform-actions">
							<Button
								variant="outline"
								disabled={
									!editor || detail.task?.schedule_state === "waiting" || detail.task?.schedule_state === "running"
								}
								onClick={() => {
									setDetail(null);
									setPlan({ day: dayKey(anchor), post: detail });
								}}
							>
								{t("publishCalendar.setTime")}
							</Button>
							{detail.plannedAt && (
								<Button
									variant="outline"
									disabled={
										!editor || detail.task?.schedule_state === "waiting" || detail.task?.schedule_state === "running"
									}
									onClick={() => void unplan(detail)}
								>
									{t("publishCalendar.removeTime")}
								</Button>
							)}
							<Button
								variant="outline"
								disabled={
									!editor ||
									(!!detail.task && !["ready", "filled", "failed", "user_reported"].includes(detail.task.state))
								}
								onClick={() => {
									setLinkEntry(detail);
									setDetail(null);
								}}
							>
								{t("publishCalendar.result")}
							</Button>
							{publishable && (
								<Button
									variant="outline"
									disabled={!editor}
									onClick={() => {
										setDetail(null);
										onEdit(detail.page, detail.platform, detail.task?.id);
									}}
								>
									{pick(locale, ["Edit content", "编辑内容"])}
								</Button>
							)}
						</div>
					</ChipModalBody>
					<ChipModalFooter
						cancelLabel={t("common.close")}
						onCancel={() => setDetail(null)}
						primaryAction={{
							label: publishable
								? t("publishCalendar.publishNow", { platform: names.get(detail.platform) ?? detail.platform })
								: pick(locale, ["Edit content", "编辑内容"]),
							disabled: !editor || !canMoveCalendarPost(detail) || detail.task?.schedule_state === "waiting",
							onClick: () => {
								if (publishable) {
									setConfirmPublish(true);
									return;
								}
								setDetail(null);
								onEdit(detail.page, detail.platform, detail.task?.id);
							},
						}}
					/>
				</ChipModal>
			)}
			{linkEntry && (
				<ResultLink
					open
					hideTrigger
					onOpenChange={(open) => {
						if (!open) setLinkEntry(null);
					}}
					title={linkEntry.title}
					value={linkEntry.url}
					disabled={!!linkEntry.task && !["ready", "filled", "failed", "user_reported"].includes(linkEntry.task.state)}
					onSave={async (url) => {
						if (linkEntry.task) await api.reportPlatformTask(entityId, linkEntry.task.id, "user_reported", url);
						else {
							const source = await api.distributionSource(entityId, linkEntry.page);
							await api.saveDistribution(entityId, {
								page_id: linkEntry.page,
								platform_id: linkEntry.platform,
								source_revision: source.revision,
								body: linkEntry.body || source.title,
								published_url: url,
							});
						}
						await queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
						setLinkEntry(null);
					}}
				/>
			)}
		</div>
	);
}

/**
 * A list of posts beside the calendar rather than under it, so a post can be
 * dragged straight onto a slot. Non-modal on purpose: a modal drawer's overlay
 * would swallow the drop.
 */
function PostsPanel({
	title,
	hint,
	rows,
	emptyLabel,
	renderPost,
	onConfirm,
	onClose,
}: {
	title: string;
	hint: string;
	rows: CalendarPost[];
	emptyLabel: string;
	renderPost: (row: CalendarPost) => ReactElement;
	/** Given, each post gets the way on to the publishing flow beside it. */
	onConfirm?: (row: CalendarPost) => void;
	onClose: () => void;
}) {
	const t = useT();
	const dragSource = useCalendarDragSource();
	return (
		<Modal
			open
			modal={false}
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<ModalContent side="right" aria-describedby={undefined} onInteractOutside={(e) => e.preventDefault()}>
				<div className="detail-panel-head">
					<ModalTitle className="detail-panel-title">{title}</ModalTitle>
					<Button variant="ghost" size="icon" aria-label={t("common.close")} onClick={onClose}>
						<X />
					</Button>
				</div>
				<div className="detail-panel-body" ref={dragSource}>
					<p className="hint">{hint}</p>
					<div className="publisher-calendar-drafts">
						{rows.map((row) =>
							onConfirm ? (
								<div key={row.id} className="publisher-calendar-draft">
									{renderPost(row)}
									<Button variant="default" onClick={() => onConfirm(row)}>
										{t("publishCalendar.confirm")}
									</Button>
								</div>
							) : (
								renderPost(row)
							),
						)}
					</div>
					{!rows.length && <p className="hint">{emptyLabel}</p>}
				</div>
			</ModalContent>
		</Modal>
	);
}

export function PlanModal({
	entityId,
	day,
	post,
	pages,
	platforms,
	onClose,
}: {
	entityId: string;
	day: string;
	post?: Pick<CalendarPost, "page" | "platform" | "title" | "plannedAt">;
	pages: { id: string; title: string; status: string }[];
	platforms: PublishingPlatform[];
	onClose: () => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const queryClient = useQueryClient();
	const id = useId();
	const [page, setPage] = useState(post?.page ?? "");
	const [platform, setPlatform] = useState(post?.platform ?? "");
	const [date, setDate] = useState(
		post?.plannedAt ? localDateTime(post.plannedAt) : day.includes("T") ? day : `${day}T09:00`,
	);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	async function save(clear = false) {
		if (busy || !editor) return;
		setBusy(true);
		setError("");
		try {
			await api.planDistribution(entityId, {
				page_id: page,
				platform_id: platform,
				planned_at: clear ? null : new Date(date).toISOString(),
			});
			await queryClient.invalidateQueries({ queryKey: [...keys.entity(entityId), "distribution"] });
			onClose();
		} catch (e) {
			setError(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}
	return (
		<ChipModal
			open
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
			dismissDisabled={busy}
			srTitle={t("publishCalendar.setTime")}
		>
			<ChipModalHeader onClose={onClose} closeAriaLabel={t("common.close")}>
				{t("publishCalendar.setTime")}
			</ChipModalHeader>
			<ChipModalBody className="px-(--s-8)">
				<form
					id={id}
					onSubmit={(e) => {
						e.preventDefault();
						void save();
					}}
				>
					<fieldset className="bare-fieldset stack" disabled={!editor || busy}>
						<div className="field">
							<Label>{t("distribution.article")}</Label>
							<SearchSelect
								disabled={!!post || !editor || busy}
								label={t("distribution.article")}
								value={page}
								onChange={setPage}
								placeholder={t("distribution.chooseArticle")}
								searchLabel={t("distribution.article")}
								emptyLabel={t("table.noMatch")}
								options={
									post
										? [{ value: post.page, label: post.title }]
										: pages.filter((p) => p.status === "published").map((p) => ({ value: p.id, label: p.title }))
								}
							/>
						</div>
						<div className="field">
							<Label>{t("distribution.platform")}</Label>
							<SearchSelect
								disabled={!!post || !editor || busy}
								label={t("distribution.platform")}
								value={platform}
								onChange={setPlatform}
								placeholder={t("distribution.choosePlatform")}
								searchLabel={t("distribution.search")}
								emptyLabel={t("table.noMatch")}
								options={platforms.map((p) => ({ value: p.id, label: p.name }))}
							/>
						</div>
						<div className="field">
							<Label htmlFor={`${id}-time`}>{t("publishCalendar.plannedTime")}</Label>
							<Input
								id={`${id}-time`}
								type="datetime-local"
								required
								value={date}
								onChange={(e) => setDate(e.target.value)}
							/>
						</div>
						<p className="hint">
							{t("publishCalendar.timezone", { zone: Intl.DateTimeFormat().resolvedOptions().timeZone })} ·{" "}
							{t("publishCalendar.help")}
						</p>
						{post?.plannedAt && (
							<Button type="button" variant="default" onClick={() => void save(true)}>
								{t("publishCalendar.removeTime")}
							</Button>
						)}
						{error && <p role="alert">{error}</p>}
					</fieldset>
				</form>
			</ChipModalBody>
			<ChipModalFooter
				onCancel={onClose}
				cancelLabel={t("common.cancel")}
				primaryAction={{
					label: t("common.save"),
					form: id,
					type: "submit",
					disabled: !editor || busy || !page || !platform || !date,
				}}
			/>
		</ChipModal>
	);
}

function CalendarContentEditor({
	viewOnly,
	entityId,
	row,
	platform,
}: {
	entityId: string;
	row: CalendarPost;
	viewOnly: boolean;
	platform?: PublishingPlatform;
}) {
	const source = useQuery({
		queryKey: [...keys.entity(entityId), "distribution-source", row.page],
		queryFn: () => api.distributionSource(entityId, row.page),
	});
	return (
		<Async state={source}>
			{(article) =>
				article &&
				platform && (
					<PublishingContentEditor
						viewOnly={viewOnly}
						source={article}
						platform={platform}
						entityId={entityId}
						existing={row.post}
						taskId={row.task?.id}
						expandedPreview
						readOnly={
							!canMoveCalendarPost(row) ||
							row.task?.schedule_state === "waiting" ||
							(!!row.task && row.task.payload.source.revision !== article.revision)
						}
					/>
				)
			}
		</Async>
	);
}
